import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  ThemeProvider,
  CssBaseline,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { Delete, Visibility } from "@mui/icons-material";
import { DELETE_TESTSUITE_REPORT_URL, GET_REPORT_VERSIONS_BY_TESTSUITEID_URL } from "envConfig";
import { Logger } from "utils/Logger";
import { useNavigate, useParams } from "react-router-dom";
import { RootState } from "state/store";
import { useSelector } from "react-redux";
import { Report } from "../../Interfaces/AutomateTest/Report";
import { useTheme } from "@emotion/react";

export const AllTestSuiteReportVersions: React.FC = () => {
  const theme = useTheme();
  const { tenantId, testSuiteId } = useParams<{ tenantId: string, testSuiteId: string }>();
  const [tenantReports, setTenantReports] = useState<Report[]>([]);
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.authentication.user);
  const [loading, setLoading] = useState<boolean>(false);

    const fetchTestSuiteReportVersions = async () => {
      setLoading(true);
      try {
        const response = await fetch(GET_REPORT_VERSIONS_BY_TESTSUITEID_URL + `?userId=${user._id}&companyId=${user.companyId}&tenantId=${tenantId}&testSuiteId=${testSuiteId}`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
            Logger.error("st-ck-client/src/modules/AutomateTest/AllTestSuiteReportVersions/AllTestSuiteReportVersions.tsx : fetchTestSuiteReportVersions(): issue while getting Report, response is " , response.statusText);
            setLoading(false);
            throw new Error(`Error: ${response.status}`);
        }

        const data = await response.json();
        if (Array.isArray(data.data)) {
            setTenantReports(data.data);
            setLoading(false);
            Logger.info("st-ck-client/src/modules/AutomateTest/AllTestSuiteReportVersions/AllTestSuiteReportVersions.tsx : fetchTestSuiteReportVersions() : Reports " , JSON.stringify(data.data, null, 2));
        } else {
            Logger.error("st-ck-client/src/modules/AutomateTest/AllTestSuiteReportVersions/AllTestSuiteReportVersions.tsx : fetchTestSuiteReportVersions() : Unexpected API response format: " , JSON.stringify(data.data, null, 2));
            setTenantReports([]);
            setLoading(false);
        }
      } catch (error) {
        Logger.error("st-ck-client/src/modules/AutomateTest/AllTestSuiteReportVersions/AllTestSuiteReportVersions.tsx : fetchTestSuiteReportVersions(): Failed to fetch reports: ", error);
        setTenantReports([]);
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchTestSuiteReportVersions();
    }, []);

  const handleDeleteTenantReport = async(_id: string) => {
    try {
        setLoading(true);
        const response = await fetch(DELETE_TESTSUITE_REPORT_URL+`/${_id}`, {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });
  
        if (!response.ok) {
            Logger.error("st-ck-client/src/modules/AutomateTest/AllTestSuiteReportVersions/AllTestSuiteReportVersions.ts : handleDeleteReport(): issue while deleting Report, response is " , response.statusText);
            setLoading(false);
            throw new Error(`Error: ${response.status}`);
        }
        await fetchTestSuiteReportVersions();
        setLoading(false);
      } catch (error) {
        Logger.error("st-ck-client/src/modules/AutomateTest/AllTestSuiteReportVersions/AllTestSuiteReportVersions.ts : handleDeleteReport(): ", "Failed to delete reports:"+ error);
        setLoading(false);
      }
  };

  return (loading ?
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Backdrop
        sx={(theme) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </ThemeProvider>
    :
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
      <Typography variant="h4" gutterBottom>
        Suite - All Versions of Test Execution Reports
      </Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>TESTSUITE</TableCell>
              <TableCell>POD</TableCell>
              <TableCell>REPORT VERSION</TableCell>
              <TableCell>TOTAL CASES</TableCell>
              <TableCell>SUCCESS RATE</TableCell>
              <TableCell>STATUS</TableCell>
              <TableCell>ACTION</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenantReports.map((report, index) => (
              <TableRow key={report._id}>
                <TableCell>{index}</TableCell>
                <TableCell>{report.name}</TableCell>
                <TableCell>{report.podId}</TableCell>
                <TableCell>{report.versionNumber}</TableCell>
                <TableCell>{[report.testCases].length || 0}</TableCell>
                <TableCell>{report.status}</TableCell>
                <TableCell>{report.status}</TableCell>
                <TableCell>
                    <>
                  <IconButton onClick={() => navigate(`/report/${report._id}/tenant/${report.tenantId}/testSuite/${report.testSuiteId}/testCases`)}>
                    <Visibility />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteTenantReport(report._id)}>
                    <Delete />
                  </IconButton>
                  </>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
